import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ArgoCDApplicationResource, GetArgoCDInstanceSummariesResponse } from "../../../../../../packages/octopus-server-client/src/index";
export type ArgoCdInstancesAndApplications = {
    instances: GetArgoCDInstanceSummariesResponse;
    applications: ArgoCDApplicationResource[];
};
export function useArgoCDContext(projectId: string | undefined) {
    return useInlineStatusQuery(async (repository) => {
        const instances = await repository.ArgoCDInstances.list();
        const queryFilter = projectId === undefined ? undefined : { projectIds: [projectId] };
        const applicationsResponsePromises = instances.Resources.map((instance) => repository.ArgoCDGateways.listApps(instance.GatewayId, queryFilter));
        const applicationResponses = await Promise.all(applicationsResponsePromises);
        const flattenedApps = applicationResponses.map((ar) => ar.Resources).reduce((accumulator, value) => accumulator.concat(value), []) ?? [];
        return { instances, applications: flattenedApps };
    }, [projectId], "List Argo CD instances");
}
export type TenantedDeploymentConfiguration = {
    //This is undefined if no environment is selected (or somehow multiple environments are)
    EnvironmentId: string | undefined;
    TenantIds: string[];
};
export type UntenantedDeploymentConfiguration = {
    EnvironmentIds: string[];
};
export type DeploymentConfiguration = UntenantedDeploymentConfiguration | TenantedDeploymentConfiguration;
function isUntenantedDeployment(filter: UntenantedDeploymentConfiguration | TenantedDeploymentConfiguration): filter is UntenantedDeploymentConfiguration {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (filter as UntenantedDeploymentConfiguration).EnvironmentIds !== undefined;
}
export function isScopedToDeploymentConfiguration(application: ArgoCDApplicationResource, projectId: string, deploymentConfiguration?: DeploymentConfiguration): boolean {
    if (deploymentConfiguration === undefined) {
        return application.Sources.some((s) => s.ProjectId === projectId);
    }
    if (isUntenantedDeployment(deploymentConfiguration)) {
        return application.Sources.some((s) => s.ProjectId === projectId && s.EnvironmentId !== null && deploymentConfiguration.EnvironmentIds.includes(s.EnvironmentId) && s.TenantId === null);
    }
    return application.Sources.some((s) => s.ProjectId === projectId && deploymentConfiguration.EnvironmentId !== undefined && s.EnvironmentId === deploymentConfiguration.EnvironmentId && s.TenantId !== null && deploymentConfiguration.TenantIds.includes(s.TenantId));
}
